// @givmo/board — every word CBG-YR6-OUTSIDE adds (a seat for someone who isn't on the board, scoped
// to their committees; frames o1–o15 of design-196), with CBG-FIX86's consent picker (f1, f1c) and
// CBG-FIX90's committee words on the director's meeting, packet and consent pages.
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them, as built. The lines marked APPROVED are shipped lines reused word for word (the
// roster dialog's own labels, the console's not-found); Jonathan approved every other line in
// CBG-YR6-COPY1's next part, its six notes as recommended (in-chat 2026-10-17 06:54 PDT, "html
// mocks approved.", then 06:55 PDT, "go with your recs";
// `scratchpad/2026-10-16-outside-copy-round/copy-review.html`). A line added or changed since is
// marked DRAFT where it sits, until its own round. Each names the frame that draws it, or "not
// drawn" for a state no frame shows. Where a line is a shipped line with the committee in the
// board's place, it says which. The lines CBG-FIX104 and CBG-FIX106 added for such a seat's own
// pages were approved on their copy page (in-chat 2026-10-17 17:42 PDT, "1 and 2, approved.";
// `scratchpad/2026-10-17-fix104-copy-round/copy-review.html`), each naming its row.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what Board records and what it opens, never a conclusion
// about what a committee or a person may lawfully do. A person is named in full; no pronoun for a
// named person.

import { houseFormName } from '../../lib/house-form';

/** The one committee's name, or null for a seat on several (whose lines say "their committees"). */
const one = (names: readonly string[]): string | null =>
  names.length === 1 ? (names[0] ?? null) : null;

export const committeeOnlyWords = {
  // ── Adding them: the committee's "Add a member" (frame o1) ──────────────────────────────────────

  /** Drawn o1: the "Who" list's last choice. */
  someoneNotOnTheBoard: 'Someone who isn’t on the board',
  /** Not drawn: someone already seated this way, offered for a second committee. */
  seatNotOnTheBoard: (name: string) => `${name} (not on the board)`,
  /** APPROVED (o1): the roster's "Add a member" labels, word for word (`seat-form-dialog.tsx`). */
  nameLabel: 'Name',
  emailLabel: 'Email of record',
  /** Drawn o1: what the seat opens, said once, before it is made. */
  scopeNote: (committee: string) =>
    `They’ll see only the ${committee}’s meetings, papers, minutes and written consents, and won’t vote on the board.`,

  // ── Where the seat appears ──────────────────────────────────────────────────────────────────────

  /** Drawn in o3's header and o14's signers; the committee page's member row, not drawn. */
  notOnTheBoard: 'Not on the board',
  /** Not drawn: such a seat recorded as former while a membership still stands. */
  noLongerServing: 'No longer serving',

  // ── Board members (frame o2) ────────────────────────────────────────────────────────────────────

  /** Drawn o2: the second group's heading (the derivation's words, §2.3). */
  groupTitle: 'Committee members who aren’t on the board',
  /** Drawn o2. */
  columnCommittees: 'Committees',
  /** Drawn o2, o3: the invite action for such a seat (the shipped "Invite to the board"). */
  inviteTo: (committees: readonly string[]) =>
    one(committees) !== null ? 'Invite to the committee' : 'Invite to their committees',

  // ── The seat's page and its invitation (frames o3, o4) ──────────────────────────────────────────

  /** Drawn o3: the card in the "Director and titles" card's place. */
  committeesCard: 'Their committees',
  /** Drawn o3: closing the card, what the seat opens (the shipped same-access line's
   *  counterpart). */
  scopeSentence:
    'Opens only these committees’ meetings, papers, minutes and written consents, and never votes on the board or counts toward its quorum.',
  /** Not drawn: the card for such a seat whose every membership has ended. */
  seatOnNoCommittee: 'Not on any committee now, so this seat opens nothing.',
  /** Not drawn: the page of such a seat recorded as former (the shipped "This seat
   *  no longer serves on the board. …"). */
  formerLine:
    'This seat no longer serves. Everything it did while it did stays on the record — a vote, a signature or an attendance entry recorded then is still that seat’s.',
  /** Drawn o3: the joining card's title (the shipped "Joining the board"). */
  joiningTitle: (committees: readonly string[]) =>
    one(committees) !== null ? 'Joining the committee' : 'Joining their committees',
  /** Drawn o4: the invitation dialog's title (the shipped "Invite {name} to the board"). */
  inviteTitle: (name: string, committees: readonly string[]) => {
    const committee = one(committees);
    return committee !== null
      ? `Invite ${name} to the ${committee}`
      : `Invite ${name} to their committees`;
  },

  // ── The invitation's landing (frame o6; the email, o5, is the backend's template) ──────────────

  /** Drawn o6, and the email's subject and heading: the shipped "{Org} invited you to its
   *  board". */
  invitedYou: (organization: string, committees: readonly string[]) => {
    const committee = one(committees);
    return committee !== null
      ? `${organization} invited you to its ${committee}`
      : `${organization} invited you to its committees`;
  },
  /** Not drawn (signed in): the shipped "Join the board". */
  joinButton: (committees: readonly string[]) =>
    one(committees) !== null ? 'Join the committee' : 'Join the committees',
  /** CBG-FIX106, row 4.8: an invitation to a committee, when another organization's sign-in rule
   *  keeps this account from joining (the shipped "… so it can’t join this board. …"). */
  lockedCantJoin: (who: string, committees: readonly string[]) =>
    `${who} manages how you sign in to this account, so it can’t join ${
      one(committees) !== null ? 'the committee' : 'the committees'
    }. Join with another Givmo account instead.`,
  /** CBG-FIX106, row 4.10: the refusal page's last line, signed out, after the link "Sign in", for
   *  an invitation this page read as a committee's (the shipped "… to open your board."). */
  signInToOpen: (committees: number) =>
    committees === 1 ? 'to open the committee.' : 'to open your committees.',
  /** Not drawn: the toast once joined (the shipped "You've joined the {Org}
   *  board."; with no organization's name, "You've joined the board."). */
  joined: (organization: string | null, committees: readonly string[]) => {
    const committee = one(committees);
    if (organization === null) {
      return committee !== null ? 'You’ve joined the committee.' : 'You’ve joined the committees.';
    }
    return committee !== null
      ? `You’ve joined the ${houseFormName(organization)} ${committee}.`
      : `You’ve joined the ${houseFormName(organization)} committees.`;
  },
  /** Not drawn: an invitation this account has already used (the shipped "You've
   *  already joined the {Org} board"; with no organization's name, "… this board"). */
  alreadyJoined: (organization: string | null, committees: readonly string[]) => {
    const committee = one(committees);
    if (organization === null) {
      return committee !== null
        ? 'You’ve already joined this committee'
        : 'You’ve already joined these committees';
    }
    return committee !== null
      ? `You’ve already joined the ${houseFormName(organization)} ${committee}`
      : `You’ve already joined the ${houseFormName(organization)} committees`;
  },

  // ── Their own pages (frames o7–o13) ─────────────────────────────────────────────────────────────

  /** Drawn o7: home's title when every seat the account holds is a committee's (no
   *  description: it would say the title again). */
  homeTitle: 'Your committees',
  /** Not drawn: an account on a board somewhere and on a committee elsewhere. */
  homeDescriptionMixed: 'The boards and committees you serve on.',
  /** Drawn o7: "Coming up"'s lead for such an account. */
  comingUpLead:
    'Dates from every committee you serve on. What each meeting is about is on its page.',
  /** CBG-FIX104, row 4.3: "Coming up"'s lead for an account on a board and on a committee
   *  elsewhere (the shipped "Dates from every board you serve on. …"). */
  comingUpLeadMixed:
    'Dates from every board and committee you serve on. What each meeting is about is on its page.',
  /** CBG-FIX104, row 4.3: nothing coming up for such a mixed account (the shipped "Nothing is
   *  scheduled on any of your boards right now."); an account of committees alone says
   *  `meetingsEmpty`. */
  nothingScheduledMixed: 'Nothing is scheduled on any of your boards or committees right now.',
  /** CBG-FIX104, row 4.4: home's card for such a seat when no organization's name can be read
   *  (the shipped "A board you serve on"). */
  unnamedSeat: 'Your committee seat',
  /** CBG-FIX104, row 4.4: a committee's row in "Coming up" when no organization's name can be
   *  read (the shipped "A board you serve on"). */
  unnamedCommittee: 'A committee you serve on',
  /** Drawn o7: the card's button (the shipped "Open this board"). */
  openButton: (committees: readonly string[]) =>
    one(committees) !== null ? 'Open the committee' : 'Open your committees',
  /** CBG-FIX106, row 4.9: an invitation's "already has a seat" step, when every seat the account
   *  holds is a committee's (the shipped "Open your board"; `openButton`'s second form). */
  openYourCommittees: 'Open your committees',
  /** Not drawn: home's card for a seat whose every membership has ended. */
  onNoCommittee: 'Not on any committee now',
  /** Drawn o8–o12: the way home (the shipped "All your boards"). */
  allYourCommittees: 'All your committees',
  /** Approved (CBG-UX-COPY3's D6), not drawn: the to-do list's empty line for such a seat, which a
   *  first visit opens (a board seat's "This is your to-do list for this board. …", D-CBG-40); no
   *  conflicts of interest form, which is the board's. */
  todoEmpty: (committees: readonly string[]) => {
    const committee = one(committees);
    return committee !== null
      ? `This is your to-do list for the ${committee}. Anything you owe the committee, like signing a written consent, will show up here when it’s assigned to you.`
      : 'This is your to-do list for your committees. Anything you owe them, like signing a written consent, will show up here when it’s assigned to you.';
  },
  /** Drawn o8: the meetings page's description (the shipped "Meetings for this board, and
   *  whether you've answered."). */
  meetingsDescription: (committees: readonly string[]) => {
    const committee = one(committees);
    return committee !== null
      ? `Meetings of the ${committee}, and whether you’ve answered.`
      : 'Meetings of your committees, and whether you’ve answered.';
  },
  /** Not drawn: nothing scheduled (the shipped "Nothing is scheduled for this board
   *  right now."). */
  meetingsEmpty: 'Nothing is scheduled for your committees right now.',
  /** CBG-FIX90, drawn o9: a committee meeting's papers card (the shipped "Everything the
   *  board was sent, …"). */
  papersLine: 'Everything the committee was sent, plus somewhere to keep your own notes.',
  /** CBG-FIX90, drawn o9: a committee meeting's reply card (the shipped "This just lets the
   *  board know what to expect. …"). */
  rsvpLine:
    'This just lets the committee know what to expect. You can change your answer any time, and it isn’t a record of who actually attended.',
  /** CBG-FIX90, not drawn (once a committee meeting starts): its record card (the
   *  shipped "What your board's secretary has written down …"). */
  recordLine: 'What the secretary has written down about this meeting.',
  /** CBG-FIX90, drawn o10: a committee meeting's packet (the shipped "Board packet" and its
   *  comments' words, the committee's). */
  packetTitle: (committee: string) => `${committee} packet`,
  privateNotesLine: (orgNoun: string) =>
    `Nobody else on the committee can read these, and neither can the ${orgNoun}’s staff. They stay attached to this version of the packet.`,
  commentsTitle: 'Comments for the committee',
  aCommitteeMember: 'A committee member',
  commentLabel: 'Say something to the committee',
  postButton: 'Post to the committee',
  /** Not drawn: Written consents' line for such a seat (the shipped "Your board can act
   *  between meetings …"). */
  consentsDescription:
    'The committee can act between meetings by every member who votes on it signing the same document. Anything waiting on you is at the top.',
  /** CBG-FIX106, row 4.13: the move's pointer and its first page, for such a seat under another
   *  organization's sign-in rule (the shipped "… so it can’t sign for this board. …", "… can’t
   *  open executive-session material for this board. …", "… stops opening this board."). */
  movePointerSign: (who: string, committees: readonly string[]) =>
    `${who} manages how you sign in to this Givmo account, so it can’t sign for ${
      one(committees) !== null ? 'the committee' : 'your committees'
    }. Move your seat to another Givmo account, and sign from there.`,
  movePointerOpen: (who: string, committees: readonly string[]) =>
    `${who} manages how you sign in to this Givmo account, so it can’t open executive-session material for ${
      one(committees) !== null ? 'the committee' : 'your committees'
    }. Move your seat to another Givmo account, and open it from there.`,
  moveStartLead: (who: string, workEmail: string, committees: readonly string[]) =>
    `${who} manages how you sign in to ${workEmail}, so it can’t sign for ${
      one(committees) !== null ? 'the committee' : 'your committees'
    }. Another Givmo account can.`,
  moveStopsOpening: (committees: readonly string[]) =>
    one(committees) !== null ? 'stops opening the committee' : 'stops opening your committees',
  /** CBG-FIX104, row 4.5: nothing there (the shipped "Nothing is waiting for you to sign on this
   *  board."), and the one committee's for a seat on none (CBG-FIX111, row 1.5). */
  consentsEmpty: (committees: readonly string[]) =>
    committees.length > 1
      ? 'Nothing is waiting for you to sign on these committees.'
      : 'Nothing is waiting for you to sign on this committee.',
  /** CBG-FIX90, not drawn (once signed): a committee consent's withdrawal line (the
   *  shipped "… while your board is still signing …"). */
  withdrawLine:
    'You can take your signature back while the committee is still signing. It doesn’t erase anything — it’s recorded as its own act, and it means the committee has to meet on this instead.',
  /** Drawn o11: the record page's way back (the shipped "All your board's records"). */
  allCommitteeRecords: 'All committee records',
  /** Not drawn: the records list for such a seat (the shipped "Your board's
   *  records" and its line). */
  recordsTitle: 'Committee records',
  recordsDescription:
    'Minutes and written consents of the committees you serve on — the ones you can read are here.',
  /** Not drawn: the record page's title while its record loads or is refused (the
   *  shipped "A record of your board"). */
  recordPageTitle: 'A committee record',
  /** APPROVED (the console's own not-found, word for word: its `NOT_FOUND_TITLE` and its not-found
   *  page's line): a board-only page — About the board, getting started, the disclosures and the
   *  year's questionnaire, the self-assessment, the copy of the return — reached by such a seat's
   *  address, which the backend answers with its one not-found (review-215's F5). */
  pageNotFoundTitle: 'Page not found',
  pageNotFoundLine: 'The link may be out of date, or the page may have moved.',
  /** Drawn o11: the acknowledgment's line (the shipped "If your board asked you to confirm
   *  …"). */
  acknowledgeLine: 'If you were asked to confirm you’ve read this, you can record that here.',

  // ── The board's lines the roster's new seats change (frame o15) ─────────────────────────────────

  /** Drawn o15: the board's minutes reminder (the shipped "Sends the seats on your roster
   *  a note …"): the roster now holds people the board's minutes don't go to. */
  boardRemindLine:
    'Sends everyone serving on the board a note that these minutes are waiting. Nothing is sent unless you send it.',
  // The board consent's not-asked line and a board seat's same-access line change where they sit
  // (`officer-roles-words.ts`: `circulateNotAsked`, `sameAccess`), approved with these.

  // ── CBG-FIX86: whose written consent first, then that body's forms (frames f1, f1b, f1c) ──────

  /** CBG-FIX86, drawn f1: the dialog's lead once the board has a committee (a board with
   *  none keeps the shipped lead). */
  pickerLead:
    'A written consent is how your board, or one of its committees, acts without holding a meeting.',
  /** CBG-FIX86, drawn f1: the blank page for a committee (the shipped "Write the consent in
   *  your board's own words."). */
  blankForCommittee: 'Write the consent in the committee’s own words.',
  /** CBG-FIX90, drawn f1c: the brackets note's end for a committee's draft (the shipped "…
   *  nothing goes to your board until you circulate it."). */
  nothingGoesToCommittee: (committee: string) =>
    `nothing goes to the ${committee} until you put it to them.`,
} as const;
